import { useAudioPlayerViewModel } from '@/features/chats/sub-features/media/viewmodel/useAudioPlayerViewModel';
import PropTypes from 'prop-types';

/**
 * AudioPlayer
 *
 * Single responsibility: own the audio ViewModel and render the hidden <audio> node.
 * Exposes playback state and handlers upward via a render prop so the parent
 * (AudioBox) can render the play button, waveform and timestamp — without
 * knowing anything about how audio works internally.
 *
 * Layering:
 *   useAudioPlayerViewModel  →  all business logic + audioRef
 *   AudioPlayer              →  calls hook, renders <audio>, passes state out
 *   AudioBox                 →  calls <AudioPlayer render={...}>, renders UI chrome
 *   AudioCard                →  layout + message metadata, zero audio knowledge
 */
const AudioPlayer = ({ src, autoPlay = false, loop = false, render }) => {
   const { audioRef, state, handlers } = useAudioPlayerViewModel();

   return (
      <>
         {/* Hidden <audio> node — display:none via .audio-player-hidden CSS class */}
         {src && (
            <audio
               ref={audioRef}
               src={src}
               autoPlay={autoPlay}
               loop={loop}
               preload="metadata"
               className="audio-player-hidden"
            >
               <track kind="captions" />
            </audio>
         )}

         {/* Render prop: gives AudioBox access to state + handlers, no prop drilling */}
         {render({ state, handlers })}
      </>
   );
};

AudioPlayer.displayName = 'AudioPlayer';

AudioPlayer.propTypes = {
   /** Audio source URL — blobUrl or remote file_url */
   src: PropTypes.string,
   autoPlay: PropTypes.bool,
   loop: PropTypes.bool,
   /**
    * Render prop receives { state, handlers } from the ViewModel.
    *
    * state:    { isPlaying, progress, currentTime }
    * handlers: { togglePlayback, handleKeyDown, handleSeekStart, handleSeek, handleSeekEnd }
    */
   render: PropTypes.func.isRequired,
};

export default AudioPlayer;
